import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerUser } from "../api/auth";
import { getPublicStats } from "../api/public";
// TODO: Re-enable OTP verification in future
// import { sendOTP, verifyOTP, resendOTP } from "../api/otp";
import "./Register.css";

export default function Register() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // Step 1: Phone/OTP, Step 2: Basic info, Step 3: Agency info
  const [role, setRole] = useState("partner"); // 'partner' or 'agency'
  
  const [stats, setStats] = useState({
    total_users: 0,
    total_trips: 0,
    total_connections: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // OTP verification states
  const [emailAddress, setEmailAddress] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    personal_photo: null,
  });
  
  // Agency-specific fields
  const [agencyDetails, setAgencyDetails] = useState({
    agency_name: "",
    location: "",
    description: "",
    services: "",
    website: "",
    logo: "",
  });
  
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'error' or 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  // OTP Timer Effect
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    } else if (otpTimer === 0 && otpSent) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpTimer, otpSent]);

  async function loadStats() {
    try {
      setStatsLoading(true);
      const data = await getPublicStats();
      setStats({
        total_users: data.total_users || 0,
        total_trips: data.total_trips || 0,
        total_connections: data.total_connections || 0
      });
    } catch (err) {
      console.error("Error loading stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }

  // TODO: Re-enable OTP verification in future
  // OTP Handlers
  /*
  async function handleSendOTP(e) {
    e.preventDefault();
    
    if (!emailAddress || emailAddress.trim() === "") {
      setMessage("Please enter a valid email address");
      setMessageType("error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailAddress)) {
      setMessage("Please enter a valid email address");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");
    
    try {
      await sendOTP(emailAddress);
      setOtpSent(true);
      setOtpTimer(60); // 60 seconds timer
      setCanResend(false);
      setMessage("OTP sent successfully! Check your email inbox.");
      setMessageType("success");
    } catch (err) {
      console.error("Error sending OTP:", err);
      setMessage(err.detail || err.message || "Failed to send OTP. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  }
  */

  // Temporary handler for email validation without OTP
  async function handleEmailVerification(e) {
    e.preventDefault();
    
    if (!emailAddress || emailAddress.trim() === "") {
      setMessage("Please enter a valid email address");
      setMessageType("error");
      return;
    }

    // Convert email to lowercase
    const normalizedEmail = emailAddress.toLowerCase().trim();
    
    // Enhanced email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(normalizedEmail)) {
      setMessage("Please enter a valid email address (e.g., user@example.com)");
      setMessageType("error");
      return;
    }

    // Check for common invalid patterns
    const invalidPatterns = [
      /\.{2,}/, // Multiple consecutive dots
      /^[.-]/, // Starting with dot or dash
      /[.-]$/, // Ending with dot or dash
      /@.*@/, // Multiple @ symbols
    ];
    
    if (invalidPatterns.some(pattern => pattern.test(normalizedEmail))) {
      setMessage("Please enter a valid email address format");
      setMessageType("error");
      return;
    }

    // Update state with normalized email
    setEmailAddress(normalizedEmail);
    setFormData({ ...formData, email: normalizedEmail });
    setMessage("Email verified! Please complete registration.");
    setMessageType("success");
    setCurrentStep(2); // Move to basic info step
  }

  /*
  async function handleVerifyOTP(e) {
    e.preventDefault();
    
    if (!otpCode || otpCode.trim() === "") {
      setMessage("Please enter the OTP code");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");
    
    try {
      await verifyOTP(emailAddress, otpCode);
      setFormData({ ...formData, email: emailAddress });
      setMessage("Email verified successfully! Please complete registration.");
      setMessageType("success");
      setCurrentStep(2); // Move to basic info step
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setMessage(err.detail || err.message || "Invalid OTP. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResendOTP() {
    setIsLoading(true);
    setMessage("");
    
    try {
      await resendOTP(emailAddress);
      setOtpTimer(60);
      setCanResend(false);
      setOtpCode("");
      setMessage("OTP resent successfully! Check your email.");
      setMessageType("success");
    } catch (err) {
      console.error("Error resending OTP:", err);
      setMessage(err.detail || err.message || "Failed to resend OTP. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  }
  */

  function onChange(e) {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  }
  
  function onAgencyDetailsChange(e) {
    setAgencyDetails({
      ...agencyDetails,
      [e.target.name]: e.target.value
    });
  }
  
  function handleNextStep(e) {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setMessage("Passwords don't match!");
      setMessageType("error");
      return;
    }
    
    if (formData.password.length < 6) {
      setMessage("Password must be at least 6 characters!");
      setMessageType("error");
      return;
    }
    
    // Move to agency details (step 3) or submit
    if (role === "agency") {
      setCurrentStep(3);
      setMessage("");
    } else {
      // For partners, submit directly
      onSubmit(e);
    }
  }
  
  async function onSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email.toLowerCase().trim()); // Ensure email is lowercase
      formDataToSend.append('password', formData.password);
      formDataToSend.append('role', role);
      
      // Add phone if provided (optional now)
      if (formData.phone && formData.phone.trim() !== "") {
        formDataToSend.append('phone', formData.phone);
      }
      
      // Add photo/logo if selected
      if (role === "partner" && formData.personal_photo) {
        formDataToSend.append('personal_photo', formData.personal_photo);
      }
      
      // Add agency-specific fields directly (not as JSON)
      if (role === "agency") {
        // Agency required fields
        formDataToSend.append('agency_name', agencyDetails.agency_name);
        formDataToSend.append('location', agencyDetails.location);
        formDataToSend.append('description', agencyDetails.description);
        formDataToSend.append('services', agencyDetails.services);
        
        // Optional fields
        if (agencyDetails.website) {
          formDataToSend.append('website', agencyDetails.website);
        }
        if (agencyDetails.logo) {
          formDataToSend.append('logo', agencyDetails.logo);
        }
      }
      
      await registerUser(formDataToSend);
      
      setMessage("Registration successful! Redirecting to login...");
      setMessageType("success");
      
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = error.response?.data?.detail || "Registration failed. Please try again.";
      setMessage(errorMessage);
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="register-container">
      {/* Left Side - Image Section */}
      <div className="register-image-section">
        <div className="overlay"></div>
        <div className="image-content">
          <h1 className="brand-title">🌍 Go With Me Buddy</h1>
          <p className="brand-subtitle">Join thousands of travelers exploring the world together</p>
          <div className="stats">
            {!statsLoading && stats.total_users >= 50 && (
              <div className="stat-item">
                <div className="stat-number">{stats.total_users}+</div>
                <div className="stat-label">Active Travelers</div>
              </div>
            )}
            {!statsLoading && stats.total_connections >= 50 && (
              <div className="stat-item">
                <div className="stat-number">{stats.total_connections}+</div>
                <div className="stat-label">Connections</div>
              </div>
            )}
            {!statsLoading && stats.total_trips >= 50 && (
              <div className="stat-item">
                <div className="stat-number">{stats.total_trips}+</div>
                <div className="stat-label">Trips Planned</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Form Section */}
      <div className="register-form-section">
        <div className="form-wrapper">
          <div className="form-header">
            <h2 className="form-title">Create Account</h2>
            <p className="form-subtitle">
              {currentStep === 1 ? "Verify your email address" : 
               currentStep === 2 ? "Complete your profile" : 
               "Tell us about your agency"}
            </p>
            {role === 'agency' && currentStep > 1 && (
              <div className="step-indicator">
                <span className={`step ${currentStep >= 2 ? 'active' : ''}`}>1</span>
                <span className="step-line"></span>
                <span className={`step ${currentStep >= 3 ? 'active' : ''}`}>2</span>
              </div>
            )}
          </div>

          {currentStep === 1 ? (
            // Step 1: Email Verification (OTP disabled for now)
            <form onSubmit={handleEmailVerification} className="register-form">
              <div className="email-verification-section">
                <div className="input-group">
                  <label htmlFor="emailAddress" className="input-label">Email Address</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📧</span>
                    <input
                      id="emailAddress"
                      name="emailAddress"
                      type="email"
                      placeholder="Enter your email address"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value.toLowerCase().trim())}
                      required
                      className="form-input"
                    />
                  </div>
                  <small className="input-hint">
                    {/* TODO: Re-enable OTP verification in future */}
                    This will be your login email
                  </small>
                </div>

                {/* TODO: Re-enable OTP verification UI in future
                {otpSent && (
                  <div className="input-group">
                    <label htmlFor="otpCode" className="input-label">Enter OTP Code</label>
                    <div className="input-wrapper">
                      <span className="input-icon">🔢</span>
                      <input
                        id="otpCode"
                        name="otpCode"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        required
                        maxLength="6"
                        className="form-input otp-input"
                      />
                    </div>
                    <small className="input-hint">
                      {otpTimer > 0 ? (
                        <span>Resend OTP in {otpTimer}s</span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendOTP}
                          className="resend-link"
                          disabled={!canResend || isLoading}
                        >
                          Resend OTP
                        </button>
                      )}
                    </small>
                  </div>
                )}
                */}
              </div>

              {message && (
                <div className={`message ${messageType}`}>
                  {message}
                </div>
              )}

              <button type="submit" className="submit-btn" disabled={isLoading}>
                {isLoading ? "Processing..." : "Continue"}
              </button>

              <p className="login-link">
                Already have an account? <Link to="/login">Sign In</Link>
              </p>
            </form>
          ) : currentStep === 2 ? (
            // Step 2: Basic Information
            <form onSubmit={handleNextStep} className="register-form">
              <div className="input-group">
                <label htmlFor="role" className="input-label">I am a</label>
                <div className="role-selector">
                  <button
                    type="button"
                    className={`role-btn ${role === 'partner' ? 'active' : ''}`}
                    onClick={() => setRole('partner')}
                  >
                    <span className="role-icon">🧳</span>
                    <span>Travel Partner</span>
                  </button>
                  <button
                    type="button"
                    className={`role-btn ${role === 'agency' ? 'active' : ''}`}
                    onClick={() => setRole('agency')}
                  >
                    <span className="role-icon">🏢</span>
                    <span>Travel Agency</span>
                  </button>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="name" className="input-label">Full Name</label>
                <div className="input-wrapper">
                  <span className="input-icon">👤</span>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={onChange}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="email" className="input-label">Email Address (Verified)</label>
                <div className="input-wrapper verified-phone">
                  <span className="input-icon">✓</span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="form-input"
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="phone" className="input-label">Phone Number (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-icon">📱</span>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1234567890 (optional)"
                    value={formData.phone}
                    onChange={onChange}
                    className="form-input"
                  />
                </div>
                <small className="input-hint">
                  Include country code (e.g., +1234567890)
                </small>
              </div>

              <div className="input-group">
                <label htmlFor="personal_photo" className="input-label">Profile Photo (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-icon">📷</span>
                  <input
                    id="personal_photo"
                    name="personal_photo"
                    type="file"
                    accept="image/*"
                    onChange={onChange}
                    className="form-input file-input"
                  />
                </div>
                {formData.personal_photo && (
                  <small className="file-name">
                    Selected: {formData.personal_photo.name}
                  </small>
                )}
              </div>

              <div className="input-group">
                <label htmlFor="password" className="input-label">Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={onChange}
                    required
                    minLength="6"
                    className="form-input"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="confirmPassword" className="input-label">Confirm Password</label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={onChange}
                    required
                    className="form-input"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={isLoading}>
                <span>
                  {isLoading && role === "partner" 
                    ? "Creating Account..." 
                    : role === "partner" 
                      ? "Create Account" 
                      : "Continue"}
                </span>
                <span className="btn-icon">
                  {isLoading && role === "partner" ? "⏳" : role === "partner" ? "✓" : "→"}
                </span>
              </button>

              {message && messageType === "error" && (
                <div className="message-box error">
                  <span className="message-icon">⚠️</span>
                  {message}
                </div>
              )}
            </form>
          ) : (
            // Step 2: Agency-specific Information (partners skip this step)
            <form onSubmit={onSubmit} className="register-form">
              {role === "agency" ? (
                // Agency Details Form
                <>
                  <div className="input-group">
                    <label htmlFor="agency_name" className="input-label">Agency Name</label>
                    <div className="input-wrapper">
                      <span className="input-icon">🏢</span>
                      <input
                        id="agency_name"
                        name="agency_name"
                        type="text"
                        placeholder="Your agency name"
                        value={agencyDetails.agency_name}
                        onChange={onAgencyDetailsChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="location" className="input-label">Location</label>
                    <div className="input-wrapper">
                      <span className="input-icon">📍</span>
                      <input
                        id="location"
                        name="location"
                        type="text"
                        placeholder="e.g., New York, USA"
                        value={agencyDetails.location}
                        onChange={onAgencyDetailsChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="description" className="input-label">Description</label>
                    <div className="input-wrapper">
                      <textarea
                        id="description"
                        name="description"
                        placeholder="Describe your agency..."
                        value={agencyDetails.description}
                        onChange={onAgencyDetailsChange}
                        required
                        className="form-input"
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="services" className="input-label">Services</label>
                    <div className="input-wrapper">
                      <textarea
                        id="services"
                        name="services"
                        placeholder="e.g., Flight booking, Hotel reservations, Tour packages"
                        value={agencyDetails.services}
                        onChange={onAgencyDetailsChange}
                        required
                        className="form-input"
                        rows="2"
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="website" className="input-label">Website (Optional)</label>
                    <div className="input-wrapper">
                      <span className="input-icon">🌐</span>
                      <input
                        id="website"
                        name="website"
                        type="url"
                        placeholder="https://yourwebsite.com"
                        value={agencyDetails.website}
                        onChange={onAgencyDetailsChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="input-group">
                    <label htmlFor="logo" className="input-label">Logo URL (Optional)</label>
                    <div className="input-wrapper">
                      <span className="input-icon">🖼️</span>
                      <input
                        id="logo"
                        name="logo"
                        type="url"
                        placeholder="https://yourlogo.png"
                        value={agencyDetails.logo}
                        onChange={onAgencyDetailsChange}
                        className="form-input"
                      />
                    </div>
                  </div>
                </>
              ) : null}

              <div className="button-group">
                <button
                  type="button"
                  className="back-btn"
                  onClick={() => setCurrentStep(1)}
                >
                  ← Back
                </button>
                <button type="submit" className="submit-btn" disabled={isLoading}>
                  <span>{isLoading ? "Creating Account..." : "Create Account"}</span>
                  {!isLoading && <span className="btn-icon">✓</span>}
                </button>
              </div>

              {message && (
                <div className={`message-box ${messageType}`}>
                  <span className="message-icon">{messageType === 'error' ? '⚠️' : '✓'}</span>
                  {message}
                </div>
              )}
            </form>
          )}

          {currentStep === 1 && (
            <>
              <div className="divider">
                <span>or sign up with</span>
              </div>

              <div className="social-login">
                <button className="social-btn google-btn">
                  <span className="social-icon">G</span>
                  Google
                </button>
                <button className="social-btn facebook-btn">
                  <span className="social-icon">f</span>
                  Facebook
                </button>
              </div>
            </>
          )}

          <p className="login-prompt">
            Already have an account? <Link to="/login" className="login-link">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}